<template>
  <div>
    <div v-if="loadingAccount" class="tw:flex tw:justify-center">
      <span class="tw:loading tw:mt-24 tw:loading-lg tw:loading-dots tw:text-gray-500" />
    </div>

    <div v-else>
      <div id="parent-account-header" class="inner">
        <div class="tw:flex tw:place-content-between tw:items-center tw:gap-5">
          <div>
            <div class="tw:mb-1 tw:flex tw:items-center tw:gap-3">
              <h1 class="tw:mb-1 tw:max-w-lg tw:truncate" data-test="account-name">{{ account.displayName }}</h1>
              <div
                v-if="IsUserAnAdmin(Store.state.user) || IsUserABroker(Store.state.user)"
                class="tw:tooltip tw:tooltip-bottom tw:flex tw:items-center"
                data-tip="View as Client">
                <ViewIcon
                  :size="16"
                  class="tw:cursor-pointer tw:stroke-capital-blue"
                  @click="handleViewAsClient(account.accountID)" />
              </div>
            </div>
            <a
              v-if="showDomain && !isAClientAccount"
              :href="`//${account.domainName}`"
              target="_blank"
              rel="noopener noreferrer"
              class="tw:lowercase"
              >{{ account.domainName }}</a
            >
            <span v-if="showDomain && !isAClientAccount" class="tw:text-md tw:text-gray-500"> &nbsp; </span>
            <span
              v-if="!isAClientAccount"
              class="tw:text-xs tw:text-gray-500 tw:uppercase"
              data-test="account-industry">
              {{ account.topLevelIndustry }}</span
            >
          </div>

          <div
            class="tw:mx-auto tw:mt-3 tw:alert tw:w-1/2 tw:alert-error"
            v-visible="!loadingKPI && errorKPI">
            <AlertCircle />
            <span>{{ errorKPI }}</span>
          </div>
          <div class="text-right statistics">
            <v-skeleton-loader type="table-row" class="tw:w-96" v-if="loadingKPI && !errorKPI" />
            <Statistics
              v-if="!loadingKPI && !errorKPI"
              dense
              no-borders
              :statistics="statistics"
              data-test="parent-kpi" />
          </div>
        </div>
      </div>

      <div class="tw:border-t tw:border-r-0 tw:border-b tw:border-l-0 tw:border-solid tw:border-gray-200">
        <v-tabs
          id="parent-account-tabs"
          show-arrows
          bg-color="grey-lighten-4"
          class="tw:uppercase"
          slide-color="primary"
          selected-class="tw:text-primary"
          v-model="currentTab"
        >
          <v-tab
            v-for="tab in accountTabs"
            :key="tab.key"
            :ripple="false"
            :value="tab.key"
            class="tw:text-xs tw:text-gray-600"
            :to="tab.route">
            {{ tab.label }}
          </v-tab>
        </v-tabs>
      </div>

      <div
        v-if="!loadingTabs && accountTabs.length === 0"
        class="tw:alert tw:p-4 tw:w-xl tw:mx-auto tw:mt-8"
      >
        <TriangleAlert size="18"/>
        <span>
        Insufficient permissions. Contact <a href="mailto:platform@cacgroup.com">platform@cacgroup.com</a>
        for assistance.
      </span>
      </div>

      <router-view v-if="!loadingAccount && accountTabs.length > 0" :account="account"></router-view>
    </div>
  </div>
</template>

<script>
import { accountService } from '@/services/api';
import Statistics from '@/components/Statistics.vue';
import { DateTime } from 'luxon';
import { compactMoney } from '@/utilities/Money';
import { compactNumber } from '@/utilities/Number';
import { ErrorLoadingKPI } from '@/utilities/Errors';
import { getElementWidth, getElementHeight } from '@/utilities/DOM';
import { Commit, Store } from '@/store';
import { Events } from '@/store/EventBus';
import { StoreMutationType } from '@/types/StoreMutationType';
import { AlertCircle, TriangleAlert, View as ViewIcon } from 'lucide-vue-next';
import { IsUserAClient, IsUserABroker, IsUserAnAdmin } from '@/utilities/AuthUtils';
import { registerResizeItem } from '@/utilities/resize';
import { useEntityTabs } from '@/composables/useEntityTabs.ts';

export default {
  name: 'ParentAccountView',
  components: { TriangleAlert, AlertCircle, Statistics, ViewIcon },
  setup() {
    const { getTabs } = useEntityTabs();

    return {
      getTabs,
    };
  },
  data: () => ({
    loadingAccount: true,
    loadingKPI: true,
    loadingTabs: true,
    accountTabs: [],
    errorKPI: undefined,
    account: {},
    statistics: [],
    includeExternal: false,
    showCurrentOnly: true,
    headerViewDimensions: { width: 0, height: 0 },
  }),
  computed: {
    showDomain() {
      if (this.account.noDomain === true) {
        return false;
      }
      return this.account.domainName;
    },
    isAClientAccount() {
      return IsUserAClient(Store.state.user);
    },
    Store() {
      return Store;
    },
    currentTab() {
      return this.$route.path.split('/')[3];
    },
  },
  methods: {
    IsUserAnAdmin,
    IsUserABroker,
    async loadAccount() {
      this.loadingAccount = true;
      this.account = await accountService.findById(this.$route.params.id);

      // fetch parent account KPIs with external policies for prospect accounts
      if (this.account.accountStatus === 'P') {
        this.includeExternal = true;
      }

      this.loadingAccount = false;
    },
    async refreshKPI() {
      try {
        this.loadingKPI = true;
        const kpi = await accountService.kpiByParentId(this.$route.params.id, this.includeExternal, this.showCurrentOnly);
        let memberSince = DateTime.fromISO(kpi.memberSince).toLocaleString(DateTime.DATE_SHORT);
        if (memberSince.includes('Invalid')) {
          memberSince = '--';
        }
        this.statistics = [
          {
            key: 'member-since',
            value: memberSince,
            heading: 'Member Since',
            loading: false,
            classes: ['dense', 'd-lg-block'],
          },
          {
            key: 'company-count',
            value: compactNumber(kpi.totalCompanyCount),
            heading: this.includeExternal === true ? 'Total Companies' : `Current Clients`,
            loading: false,
            classes: ['dense'],
          },
          {
            key: 'total-premium',
            value: compactMoney(kpi.totalPremium),
            heading: this.includeExternal === true ? 'Total Premium' : `Current Premium`,
            loading: false,
            classes: ['dense'],
            toolTip: 'Includes BOR Premium',
          },
          {
            key: 'policy-count',
            value: compactNumber(kpi.totalPolicyCount),
            heading: this.includeExternal === true ? 'Total Policies' : `Current Policies`,
            loading: false,
            classes: ['dense'],
          },
        ];
      } catch (err) {
        console.error(err);
        this.errorKPI = ErrorLoadingKPI;
      } finally {
        this.loadingKPI = false;
      }
    },
    async handleViewAsClient(accountID) {
      Commit(StoreMutationType.ENABLE_CLIENT_VIEW_MODE, {
        id: -1,
        name: '',
        allowedAccountIds: [accountID],
      });

      await this.loadAccount();
      await this.refreshKPI();
    },
    async loadParentAccountTabs() {
      this.loadingTabs = true;
      try {
        this.accountTabs = await this.getTabs('parent-account', this.account.accountID.toString());
      } catch (error) {
        console.error('Error loading parent account tabs:', error);
        this.accountTabs = [];
      } finally {
        this.loadingTabs = false;
      }
    },
  },
  watch: {
    async isAClientAccount() {
      // when client view is exited, fetch KPIs and display
      await this.loadAccount();
      await this.refreshKPI();
      await this.loadParentAccountTabs();
    },
    accountTabs() {
      if (!this.accountTabs.map((t) => t.key).includes(this.currentTab)) {
        if (this.accountTabs.length > 0) {
          this.$router.push(this.accountTabs[0].route);
        }
      }
    }
  },

  // -----------
  // lifecycles
  // -----------
  async created() {
    await this.loadAccount();
    await this.refreshKPI();
    await this.loadParentAccountTabs();
    addEventListener(Events.REFRESH_ACCOUNT_KPI, this.refreshKPI);
  },
  mounted() {
    registerResizeItem({ name: 'parent-account-header', selector: 'parent-account-header' });
    registerResizeItem({ name: 'parent-account-tabs', selector: 'parent-account-tabs' });
  },
  beforeUnmount() {
    removeEventListener(Events.REFRESH_ACCOUNT_KPI, this.refreshKPI);
  },
  updated() {
    const parentAccountHeader = document.getElementById('parent-account-header');
    const parentAccountTabs = document.getElementById('parent-account-tabs');
    this.headerViewDimensions.width = getElementWidth(parentAccountHeader) + getElementWidth(parentAccountTabs);
    this.headerViewDimensions.height = getElementHeight(parentAccountHeader) + getElementHeight(parentAccountTabs);
  },
};
</script>
